import {Alert, Button, Group, Paper, Select, Stack, Text, TextInput} from '@mantine/core'
import {useForm} from '@mantine/form'
import {notifications} from '@mantine/notifications'
import {IconTool} from '@tabler/icons-react'
import {useState} from 'react'

import {useCurrentUser} from 'src/auth/use-current-user'
import {type OutOfServiceReason, outOfServiceReasonOptions} from 'src/dispatch/availability/schemas'
import {
    useBackInService,
    useMarkOutOfService,
    useOutOfServiceEquipment,
} from 'src/dispatch/availability/use-availability'
import {canChangeYardItem} from 'src/jobs/edit-rights'
import {formatJobDate} from 'src/jobs/job-schedule-format'
import {alertToneColor} from 'src/theme/semantic-colors'

type OutOfServiceFormValues = {
    reason: OutOfServiceReason
    note: string
    expectedBack: string
}

type OutOfServicePanelProps = {
    rentalAssetId: string
    yardId?: string | null
}

export function OutOfServicePanel({rentalAssetId, yardId = null}: OutOfServicePanelProps) {
    const currentUser = useCurrentUser()
    const canChange = currentUser == null || canChangeYardItem(currentUser, yardId)
    const {data: open = []} = useOutOfServiceEquipment()
    const markOut = useMarkOutOfService()
    const markBack = useBackInService()
    const [editing, setEditing] = useState(false)
    const form = useForm<OutOfServiceFormValues>({
        initialValues: {reason: 'broken', note: '', expectedBack: ''},
    })
    const current = open.find((row) => row.rentalAssetId === rentalAssetId)

    async function runOutOfServiceAction(action: () => Promise<unknown>, done: string) {
        try {
            await action()
            notifications.show({title: done, message: '', color: alertToneColor('success')})
            setEditing(false)
        } catch (error) {
            notifications.show({
                title: 'Could not save',
                message: error instanceof Error ? error.message : 'Try again.',
                color: alertToneColor('danger'),
            })
        }
    }

    if (current) {
        return (
            <Alert
                color="red"
                variant="light"
                icon={<IconTool size={20} />}
                title={`${current.label} since ${formatJobDate(current.since)}`}
                data-testid="out-of-service-panel"
            >
                <Stack gap="xs">
                    {current.note ? <Text size="sm">{current.note}</Text> : null}
                    {current.expectedBack ? (
                        <Text size="sm">Expected back {formatJobDate(current.expectedBack)}</Text>
                    ) : null}
                    <Text size="sm">Cannot be assigned to jobs until back in service</Text>
                    {canChange ? (
                        <Group>
                            <Button
                                color="green"
                                loading={markBack.isPending}
                                onClick={() =>
                                    void runOutOfServiceAction(
                                        () => markBack.mutateAsync(rentalAssetId),
                                        'Back in service',
                                    )
                                }
                            >
                                Back in service
                            </Button>
                        </Group>
                    ) : null}
                </Stack>
            </Alert>
        )
    }

    if (!canChange) {
        return null
    }

    if (!editing) {
        return (
            <Group>
                <Button
                    variant="light"
                    color="red"
                    leftSection={<IconTool size={16} />}
                    onClick={() => setEditing(true)}
                >
                    Mark out of service
                </Button>
            </Group>
        )
    }

    return (
        <Paper withBorder p="md" radius="md" data-testid="out-of-service-form">
            <form
                onSubmit={form.onSubmit(
                    (values) =>
                        void runOutOfServiceAction(
                            () => markOut.mutateAsync({rentalAssetId, input: values}),
                            'Marked out of service',
                        ),
                )}
            >
                <Stack gap="sm">
                    <Select
                        label="Reason"
                        data={outOfServiceReasonOptions}
                        allowDeselect={false}
                        {...form.getInputProps('reason')}
                    />
                    <TextInput label="Note (optional)" {...form.getInputProps('note')} />
                    <TextInput
                        type="date"
                        label="Expected back (optional)"
                        {...form.getInputProps('expectedBack')}
                    />
                    <Group justify="flex-end">
                        <Button type="button" variant="default" onClick={() => setEditing(false)}>
                            Cancel
                        </Button>
                        <Button type="submit" color="red" loading={markOut.isPending}>
                            Mark out of service
                        </Button>
                    </Group>
                </Stack>
            </form>
        </Paper>
    )
}
